/**
 * @fileoverview Featured Album — reserve a second quote line only where one is used.
 *
 * The cards in a row are independent columns, so a quote that wraps to two lines
 * pushes its own divider down and out of line with its neighbor's. Reserving the
 * second line in CSS fixes that, but costs a blank line under every quote that
 * didn't need it, and CSS has no way to ask whether the text actually wrapped.
 *
 * So the line count is measured here and the reserve is applied per row, leaving
 * rows whose quotes all fit on one line at their natural height. With JS
 * unavailable nothing is applied and the quotes size to their content — the
 * dividers can sit a line apart, which is the pre-existing behavior.
 */

(function () {
  // The theme editor replaces a section's markup wholesale on
  // `shopify:section:load`, including this <script src> tag, which the browser
  // re-fetches (from cache) and re-executes. Without a guard, each re-render
  // stacks another `resize` listener and another `shopify:section:load`
  // listener, so `update()` runs once per accumulated handler. Since there's
  // no per-section instance to key a WeakMap on (no Swiper, just page-wide
  // listeners), the whole listener-registration block below is guarded once
  // on `window` instead.
  if (window.featuredAlbumListenersAttached) return;
  window.featuredAlbumListenersAttached = true;

  const RESERVE = 'featured-album__quote--two-lines';

  /**
   * Line boxes in an element, counted from the client rects of a range over its
   * contents — one rect per line. Reading the rects rather than the height keeps
   * this independent of any height the reserve class has already applied.
   *
   * @param {Element} el
   * @returns {number}
   */
  function lineCount(el) {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getClientRects().length;
  }

  /**
   * Cards grouped into the rows they're laid out in. The column count changes
   * with the breakpoint, so the rows are read back off the layout rather than
   * assumed — grid items in one row all share a top edge.
   *
   * @param {NodeListOf<HTMLElement>} cards
   * @returns {HTMLElement[][]}
   */
  function byRow(cards) {
    const rows = new Map();

    cards.forEach(function (card) {
      const top = Math.round(card.offsetTop);
      if (!rows.has(top)) rows.set(top, []);
      rows.get(top).push(card);
    });

    return Array.from(rows.values());
  }

  /** @param {Element} grid */
  function update(grid) {
    byRow(grid.querySelectorAll('[data-featured-album-card]')).forEach(function (row) {
      const quotes = row
        .map(function (card) {
          return card.querySelector('[data-featured-album-quote]');
        })
        .filter(Boolean);

      const wrapped = quotes.some(function (quote) {
        return lineCount(quote) > 1;
      });

      quotes.forEach(function (quote) {
        quote.classList.toggle(RESERVE, wrapped);
      });
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-featured-album-grid]').forEach(update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // A narrower column wraps quotes that fit a moment ago, and vice versa.
  let frame = null;
  window.addEventListener('resize', function () {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(function () {
      frame = null;
      init();
    });
  });

  // The first measurement can land on the fallback font, which wraps differently
  // from the webfont that replaces it.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      init();
    });
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
